import React from "react";
import { ApiError } from "../../../app/api/client";
import type { PersonDto } from "../types";
import { HTTP_STATUS_CONFLICT, HTTP_STATUS_REQUEST_TIMEOUT } from "../../../app/constants/httpStatus";

/** Wartezeit auf den Chip am Lesegerät (Sekunden). */
const SCAN_TIMEOUT_SECONDS = 60;
/** Fehlercode des Backends, wenn das Medium bereits einer anderen Person oder einem Konto zugeordnet ist. */
const MEDIUM_ALREADY_ASSIGNED_CODE = "medium_already_assigned";

type AccessMediumReloadRef = React.MutableRefObject<{ reload?: () => void } | null>;

type AccessMediumScanConflict = {
  mediumId: string;
  accessMediumId: string;
  personId: string;
  personName: string;
};

type UseAccessMediumScanOptions = {
  person: PersonDto | null;
  personId: string;
  subjectKind?: "person" | "account";
  readerDeviceHardwareId?: string | null;
  studioId?: string | null;
  readerDeviceHardwareIds?: string[];
  canStart?: boolean;
  api: {
    req: (url: string, init?: RequestInit) => Promise<unknown>;
  };
  message: {
    success: (content: React.ReactNode) => unknown;
  };
  text: (key: string) => string;
  accessMediaRef: AccessMediumReloadRef;
};

export function useAccessMediumScan({
  person,
  personId,
  subjectKind = "person",
  readerDeviceHardwareId,
  studioId,
  readerDeviceHardwareIds,
  canStart = true,
  api,
  message,
  text,
  accessMediaRef
}: UseAccessMediumScanOptions) {
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);
  const [scanHint, setScanHint] = React.useState<string | null>(null);
  const [scanConflict, setScanConflict] = React.useState<AccessMediumScanConflict | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);

  const close = React.useCallback(() => {
    try {
      abortRef.current?.abort();
    } catch {
      // Ein bereits beendeter Abbruch-Controller wirft; das ist hier der Normalfall.
    }
    abortRef.current = null;
    setOpen(false);
    setSaving(false);
    setScanHint(null);
    setScanConflict(null);
  }, []);

  // Ein Scan pro Öffnung/Lesergruppe. Speichern- und Hinweiszustände starten keine neue Sitzung.
  const callbacks = React.useRef({ message, text, close, accessMediaRef });
  React.useLayoutEffect(() => { callbacks.current = { message, text, close, accessMediaRef }; });
  const readerIdsKey = readerDeviceHardwareIds === undefined ? null : JSON.stringify(readerDeviceHardwareIds);
  const hasPerson = !!person;
  React.useEffect(() => {
    if (!open || !hasPerson || !canStart) return;
    const ac = new AbortController();
    abortRef.current = ac;
    const run = async () => {
      setSaving(true);
      setScanHint(null);
      setScanConflict(null);
      try {
        const payload = {
          personId: subjectKind === "person" ? personId : null,
          accountId: subjectKind === "account" ? personId : null,
          readerDeviceHardwareId: readerDeviceHardwareId || null,
          studioId: studioId || null,
          readerDeviceHardwareIds: readerIdsKey === null ? undefined : JSON.parse(readerIdsKey) as string[],
          timeoutSeconds: SCAN_TIMEOUT_SECONDS
        };
        await api.req("/api/v1/access-media/scan", {
          method: "POST",
          body: JSON.stringify(payload),
          signal: ac.signal
        });

        if (ac.signal.aborted) return;
        callbacks.current.message.success(callbacks.current.text("Access medium saved."));
        callbacks.current.close();
        callbacks.current.accessMediaRef.current?.reload?.();
      } catch (e: unknown) {
        if (ac.signal.aborted) return;

        if (e instanceof ApiError) {
          if (e.status === HTTP_STATUS_REQUEST_TIMEOUT) {
            setScanHint(callbacks.current.text("No chip detected. Please present the chip to the reader again."));
            return;
          }

          if (e.status === HTTP_STATUS_CONFLICT) {
            try {
              const parsed = JSON.parse(e.bodyText || "{}");
              if (parsed?.code === MEDIUM_ALREADY_ASSIGNED_CODE && parsed?.mediumId && (parsed?.personId || parsed?.accountId)) {
                setScanConflict({
                  mediumId: String(parsed.mediumId),
                  accessMediumId: String(parsed.accessMediumId || ""),
                  personId: String(parsed.personId || parsed.accountId || ""),
                  personName: String(parsed.personName || "")
                });
                return;
              }
            } catch {
              // ignore malformed API body
            }
          }
        }
        setScanHint(callbacks.current.text("Access medium scan failed."));
      } finally {
        if (abortRef.current === ac) {
          abortRef.current = null;
          setSaving(false);
        }
      }
    };
    void run();
    return () => {
      ac.abort();
      if (abortRef.current === ac) abortRef.current = null;
    };
  }, [open, hasPerson, canStart, personId, subjectKind, readerDeviceHardwareId, readerIdsKey, studioId, api, attempt]);

  return {
    open,
    setOpen,
    saving: open && canStart && saving,
    setSaving,
    scanHint,
    scanConflict,
    close,
    retry: () => setAttempt(value => value + 1)
  };
}
